import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@lastrouter/ui';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { KeyValue, PageHeader, QueryState, StatusBadge } from '../components/common.js';
import { formatDate, formatDateTime, formatDuration } from '../lib/format.js';
import { GlobalLimit } from './limits.js';
import { licenseTone } from './overview.js';

/** YAML legível do objeto de configuração (só leitura, já sem segredos). */
export function toYaml(value: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value
      .map((v) => {
        const inner = toYaml(v, indent + 1);
        return typeof v === 'object' && v !== null ? `${pad}-\n${inner}` : `${pad}- ${inner}`;
      })
      .join('\n');
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries
      .map(([k, v]) => (typeof v === 'object' && v !== null && (Array.isArray(v) ? v.length : Object.keys(v).length) ? `${pad}${k}:\n${toYaml(v, indent + 1)}` : `${pad}${k}: ${toYaml(v, indent + 1)}`))
      .join('\n');
  }
  return value === null || value === undefined ? 'null' : String(value);
}

export function SystemPage() {
  const { t } = useTranslation();
  const q = useApiQuery('system');
  return (
    <AdminOnly>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <QueryState query={q}>
        {(s) => (
          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
            <div className="lg:col-span-2">
              <GlobalLimit />
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Lastrouter</CardTitle>
              </CardHeader>
              <CardContent>
                <KeyValue
                  items={[
                    { label: t('settings.version'), value: s.version },
                    { label: t('settings.edition'), value: t(`settings.editions.${s.edition}`) },
                    { label: t('settings.instance'), value: s.instance_id },
                    { label: t('settings.tenant'), value: s.tenant_id },
                    { label: t('settings.backend'), value: t(`settings.backends.${s.state_backend}`) },
                    { label: t('settings.uptime'), value: formatDuration(s.uptime_seconds) },
                  ]}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.license')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div>
                  <StatusBadge tone={licenseTone(s.license.status)}>{t(`license.${s.license.status}`)}</StatusBadge>
                </div>
                <KeyValue
                  items={[
                    { label: t('settings.customer'), value: s.license.cliente ?? '-' },
                    { label: t('settings.validity'), value: formatDate(s.license.validade) },
                    { label: t('settings.features'), value: s.license.recursos.join(', ') || '-' },
                    { label: t('settings.lastCheckin'), value: formatDateTime(s.license.last_checkin_at) },
                  ]}
                />
                <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{s.license.detail}</p>
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t('settings.config')}</CardTitle>
                <CardDescription>{t('common.readOnly')}</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="max-h-[480px] overflow-auto rounded-[var(--lr-radius-md)] bg-sunken p-4 font-mono text-[length:var(--lr-text-sm)] text-fg" tabIndex={0} aria-label={t('settings.config')}>
                  {toYaml(s.config)}
                </pre>
              </CardContent>
            </Card>
          </div>
        )}
      </QueryState>
    </AdminOnly>
  );
}
